import { ApiEventType, type EventResponse } from '@/core/generated/api';
import { formatDateTime } from '@/core/format';
import type { ColumnConfig } from '@/shared/ui/DataTable/DataTable';
import { EventEntityLink } from '../ui/EventEntityLink';

const executorEventText: Partial<Record<ApiEventType, readonly [string, string, string?]>> = {
    [ApiEventType.ExecutorAccepted]: ['откликнулся на', 'заказ'],
    [ApiEventType.ExecutorAssigned]: ['назначен на', 'заказ'],
    [ApiEventType.ExecutorAssignedByAdmin]: ['назначен на', 'заказ', 'администратором'],
    [ApiEventType.ExecutorSignedOrderTask]: ['подписал договор', 'заказа'],
    [ApiEventType.ExecutorSignedAcceptanceAct]: ['подписал акт выполненных работ по', 'заказу'],
    [ApiEventType.ExecutorApprovedByClient]: ['одобрен клиентом на', 'заказ'],
    [ApiEventType.ExecutorApprovedByAdmin]: ['одобрен администратором на', 'заказ'],
    [ApiEventType.ExecutorRejectedOrder]: ['отказался от', 'заказа'],
    [ApiEventType.ExecutorRemovedByAdmin]: ['снят с', 'заказа', 'оператором'],
};

/** Показывает сохранённое описание или ссылки на участников события */
const eventDescription = (event: EventResponse) => {
    if (event.description?.trim()) {
        return <>
            {event.description}
            {event.promoCodePrintingId &&
                <> · <EventEntityLink to={`/promo-code-printings/${event.promoCodePrintingId}`}>Тираж</EventEntityLink></>
            }
            {event.orderId &&
                <> · <EventEntityLink to={`/orders/${event.orderId}`}>Заказ</EventEntityLink></>
            }
        </>;
    }

    const executorId = event.executorId;

    const promoCodeId = event.promoCodeId;

    const clientSubscriptionId = event.clientSubscriptionId;

    const eventType = event.type?.id;
    const executorAction = eventType && executorEventText[eventType];

    const isSubscriptionEvent = eventType === ApiEventType.SubscriptionApplied
        || eventType === ApiEventType.SubscriptionActivated
        || eventType === ApiEventType.SubscriptionUsageReturned;

    if (executorAction) {
        const [action, orderLabel, suffix] = executorAction;

        return <>{executorId
            ? <EventEntityLink to={`/executors/${executorId}`}>Исполнитель</EventEntityLink>
            : 'Исполнитель'
        } {action} {event.orderId
            ? <EventEntityLink to={`/orders/${event.orderId}`}>{orderLabel}</EventEntityLink>
            : orderLabel
            }{suffix &&
                <> {suffix}</>
            }
        </>;
    }

    const isPenaltyEvent = eventType === ApiEventType.ExecutorPenaltyAssignedByAdmin
        || eventType === ApiEventType.ExecutorPenaltyRemovedByAdmin;

    if (isPenaltyEvent) {
        return <>
            {eventType === ApiEventType.ExecutorPenaltyAssignedByAdmin
                ? 'Назначен штраф '
                : 'Снят штраф '
            }
            {executorId
                ? <EventEntityLink to={`/executors/${executorId}`}>
                    {eventType === ApiEventType.ExecutorPenaltyAssignedByAdmin
                        ? 'исполнителю'
                        : 'исполнителя'
                    }
                </EventEntityLink>
                : eventType === ApiEventType.ExecutorPenaltyAssignedByAdmin
                    ? 'исполнителю'
                    : 'исполнителя'
            } администратором
        </>;
    }

    if (eventType === ApiEventType.ClientRefusedExecutor) {
        return <>Клиент отказался от {executorId
            ? <EventEntityLink to={`/executors/${executorId}`}>исполнителя</EventEntityLink>
            : 'исполнителя'
        } по {event.orderId
            ? <EventEntityLink to={`/orders/${event.orderId}`}>заказу</EventEntityLink>
            : 'заказу'
            }</>;
    }

    if (eventType === ApiEventType.PaymentSentToExecutor) {
        return <>Оплата направлена {executorId
            ? <EventEntityLink to={`/executors/${executorId}`}>исполнителю</EventEntityLink>
            : 'исполнителю'
        } по {event.orderId
            ? <EventEntityLink to={`/orders/${event.orderId}`}>заказу</EventEntityLink>
            : 'заказу'
            }</>;
    }

    if (eventType === ApiEventType.PromoCodeApplied) {
        return <>
            {promoCodeId
                ? <EventEntityLink to={`/promo-codes/${promoCodeId}`}>Промокод</EventEntityLink>
                : 'Промокод'
            } применён к {event.orderId
                ? <EventEntityLink to={`/orders/${event.orderId}`}>заказу</EventEntityLink>
                : 'заказу'
            }
        </>;
    }

    if (eventType === ApiEventType.ReviewCreated && event.orderId) {
        return <>Отзыв оставлен к <EventEntityLink to={`/orders/${event.orderId}`}>заказу</EventEntityLink></>;
    }

    if (eventType === ApiEventType.ReviewDeleted) {
        return <>
            Отзыв {executorId
                ? <EventEntityLink to={`/executors/${executorId}`}>исполнителя</EventEntityLink>
                : 'исполнителя'
            } {event.orderId
                ? <>по <EventEntityLink to={`/orders/${event.orderId}`}>заказу</EventEntityLink> </>
                : null
            }удалён
        </>;
    }

    if (isSubscriptionEvent && eventType) {
        const action = {
            [ApiEventType.SubscriptionApplied]: 'применён к',
            [ApiEventType.SubscriptionActivated]: 'приобретён по',
            [ApiEventType.SubscriptionUsageReturned]: 'возвращено после отмены',
        }[eventType];

        const usageReturned = eventType === ApiEventType.SubscriptionUsageReturned;

        const prefix = usageReturned
            ? 'Использование '
            : '';

        const subscriptionName = usageReturned
            ? 'абонемента'
            : 'Абонемент';

        const orderLabel = eventType === ApiEventType.SubscriptionUsageReturned
            ? 'заказа'
            : 'заказу';

        return <>
            {prefix}
            {clientSubscriptionId
                ? <EventEntityLink to={`/client-subscriptions/${clientSubscriptionId}`}>
                    {subscriptionName}
                </EventEntityLink>
                : subscriptionName
            } {action} {event.orderId
                ? <EventEntityLink to={`/orders/${event.orderId}`}>{orderLabel}</EventEntityLink>
                : orderLabel
            }
        </>;
    }

    return event.orderId
        ? <EventEntityLink to={`/orders/${event.orderId}`}>Заказ</EventEntityLink>
        : event.description;
};

/** Колонки истории событий профиля */
export const EVENT_TABLE_COLUMNS: ColumnConfig<EventResponse>[] = [
    {
        field: 'type.name',
        filter: true,
        header: 'Тип события',
        style: {
            minWidth: '12rem',
            textAlign: 'left'
        }
    },
    {
        field: 'created',
        filter: true,
        body: (row) => row.createdLocalTime && formatDateTime(row.createdLocalTime) || formatDateTime(row.created, true),
        dataType: 'date',
        sortable: true,
        header: 'Дата и время события',
        style: {
            minWidth: '12rem',
            textAlign: 'center'
        }
    },
    {
        field: 'comment',
        filter: true,
        header: 'Комментарий',
        style: {
            minWidth: '12rem',
            textAlign: 'left'
        }
    },
    {
        field: 'description',
        filter: true,
        body: eventDescription,
        header: 'Описание',
        style: {
            minWidth: '14rem',
            textAlign: 'left'
        }
    },
    {
        field: 'userName',
        filter: true,
        header: 'Пользователь',
        style: {
            minWidth: '12rem',
            textAlign: 'center'
        }
    }
];

